import { createRoot, type Remix } from "@remix-run/dom";
import { events } from "@remix-run/events";
import { press } from "@remix-run/events/press";
import { arrowUp, arrowDown, arrowLeft, arrowRight, escape } from "@remix-run/events/key";
import { Game2048 } from "./game.ts";
import {
  Layout,
  Header,
  Title,
  ScoreDisplay,
  Tile,
  GameButton,
  GameMessage,
  MessageText,
  HelpText,
} from "./components.tsx";

function Game2048App(this: Remix.Handle<Game2048>) {
  let game = new Game2048();

  events(game, [Game2048.change(() => this.render())]);

  events(document, [
    arrowUp(() => {
      game.makeMove("up");
    }),
    arrowDown(() => {
      game.makeMove("down");
    }),
    arrowLeft(() => {
      game.makeMove("left");
    }),
    arrowRight(() => {
      game.makeMove("right");
    }),
    escape(() => {
      game.resetGame();
    }),
  ]);

  this.context.set(game);

  return () => (
    <Layout>
      <Header>
        <Title />
        <ScoreDisplay score={game.score} />
      </Header>
      
      <GameBoard />
      
      <Controls />
      
      <HelpText />
    </Layout>
  );
}

function GameBoard(this: Remix.Handle) {
  let game = this.context.get(Game2048App);

  return () => (
    <div
      css={{
        position: "relative",
        background: "#2a2a2a",
        borderRadius: "16px",
        padding: "16px",
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.3)",
      }}
    >
      <div
        css={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gridTemplateRows: "repeat(4, 1fr)",
          gap: "16px",
        }}
      >
        {game.board.map((row, rowIndex) =>
          row.map((cell, colIndex) => (
            <Tile key={`${rowIndex}-${colIndex}`} value={cell} />
          ))
        )}
      </div>

      {game.isWon && !game.isGameOver && (
        <GameMessage>
          <MessageText>You Win! 🎉</MessageText>
          <div
            css={{
              display: "flex",
              gap: "12px",
            }}
          >
            <GameButton
              on={[
                press(() => {
                  game.continueGame();
                }),
              ]}
            >
              Keep Playing
            </GameButton>
            <GameButton
              on={[
                press(() => {
                  game.resetGame();
                }),
              ]}
            >
              New Game
            </GameButton>
          </div>
        </GameMessage>
      )}

      {game.isGameOver && (
        <GameMessage>
          <MessageText>Game Over!</MessageText>
          <GameButton
            on={[
              press(() => {
                game.resetGame();
              }),
            ]}
          >
            Try Again
          </GameButton>
        </GameMessage>
      )}
    </div>
  );
}

function Controls(this: Remix.Handle) {
  let game = this.context.get(Game2048App);

  return () => (
    <div
      css={{
        display: "flex",
        justifyContent: "center",
        gap: "12px",
      }}
    >
      <GameButton
        on={[
          press(() => {
            game.resetGame();
          }),
        ]}
      >
        New Game
      </GameButton>
    </div>
  );
}

createRoot(document.body).render(<Game2048App />);

